import { createElement } from "react";
import { createStore } from "zustand/vanilla";
import { setupQueryInvalidation } from "../../runtime/transport/queryInvalidation";
import { BaseModuleComponent } from "./BaseModuleComponent";
import { BaseEntityCard } from "./BaseEntityCard";
import { registerSchemaVisuals } from "./schemaIcons";
import type { ModuleDefinition } from "../../runtime/contracts/module";
import type { ModuleConfig, ModuleQueryKeys } from "./types";
import type {
  AgentHistoryBlock,
  EntityRendererRegistration,
  AgentHistoryRendererRegistration,
  ModuleAgentContribution,
} from "../../runtime/contracts/agent";

export interface BaseModuleStoreState {
  selectedId: string | undefined;
  searchQuery: string;
  [key: string]: unknown;
}

/**
 * Define a module from config. Returns a ModuleDefinition with all
 * standard behavior built in — list, detail, router, context menu,
 * prefetch, invalidation, agent contributions.
 *
 * Override only what's different.
 */
export function defineModule(config: ModuleConfig): ModuleDefinition {
  const schemas = config.schemas ?? config.entityTypes.map((t) => `${config.id}.${t}`);

  // ── Register schema visuals ──
  // Skip only when schemas are overridden AND no entityLabels provided
  // (e.g. inbox reuses episodes.episode visuals registered by Episodes module).
  // When entityLabels ARE provided with custom schemas (e.g. files: "file.object"),
  // the module owns its visuals and must register them.
  if (!config.schemas || config.entityLabels) {
    registerSchemaVisuals(
      config.entityTypes.map((entityType, i) => {
        const custom = config.entityLabels?.[entityType];
        // Capitalize entity type as default label: "person" → "Person"
        const defaultLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);
        return {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          schemaId: schemas[i]!,
          entry: {
            icon: custom?.icon ?? config.iconName,
            label: custom?.label ?? defaultLabel,
            tabLabel: custom?.tabLabel,
            themeColor: config.themeColor,
          },
        };
      }),
    );
  }

  const queryKeys: ModuleQueryKeys = {
    all: [config.id] as const,
    list: (params?) => [config.id, "list", params] as const,
    detail: (id: string) => [config.id, "detail", id] as const,
  };

  // ── Entity renderers ──
  // Single Render per schema (see docs/frontend/module-standard.md
  // "ONE COMPONENT PER ENTITY"). `hasMore` only gates the chevron; the
  // expanded layout lives inside the same component, switched via
  // ExpansionContext.
  const entityRenderers: EntityRendererRegistration[] = schemas.map(
    (schemaId, i) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const entityType = config.entityTypes[i]!;
      const perType = config.entityLabels?.[entityType];
      const hasMore = perType?.hasMore ?? config.hasMore;
      return {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        id: `${config.id}-${entityType}`,
        moduleId: config.id,
        schemaMatch: schemaId,
        Render: perType?.EntityCard ?? config.EntityCard ?? BaseEntityCard,
        ...(hasMore ? { hasMore } : {}),
      };
    },
  );

  // ── History renderers (tool call renderers) ──
  const historyRenderers: AgentHistoryRendererRegistration[] = [];
  if (config.toolCallRenderers) {
    for (const reg of config.toolCallRenderers) {
      // Match dot and underscore variants: notes.update + notes_update
      // Also match without trailing 's' for modules like emails → email.send
      const variants: string[] = [config.id];
      if (config.id.endsWith("s")) {
        variants.push(config.id.slice(0, -1));
      }
      // A nested action like `messages.reply` has to match every spelling the
      // agent actually emits. Substituting only the joining separator leaves
      // `telegram_messages.reply` — a form nothing produces — while the real
      // wire name `telegram_messages_reply` matched nothing and fell through
      // to the generic "Agent wants to: …" card. So flatten the inner dots too.
      const fullActions = new Set(
        variants.flatMap((prefix) =>
          reg.actions.flatMap((a) => [
            `${prefix}.${a}`,
            `${prefix}_${a}`,
            `${prefix}_${a.replace(/\./g, "_")}`,
          ]),
        ),
      );
      historyRenderers.push({
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        id: `${config.id}-tool-${reg.actions[0]}`,
        moduleId: config.id,
        match: (block: AgentHistoryBlock) =>
          block.toolName != null && fullActions.has(block.toolName),
        Render: reg.Render as never,
        priority: 10,
      });
    }
  }

  // ── Agent contribution ──
  const agent: ModuleAgentContribution & {
    entityRenderers: EntityRendererRegistration[];
  } = {
    systemPrompt: config.systemPrompt,
    entityRenderers,
    historyRenderers: historyRenderers.length > 0 ? historyRenderers : undefined,
    navigateToEntity: config.navigateToEntity,
    extractAllowlistTarget: config.extractAllowlistTarget,
    onDraftRequest: config.onDraftRequest,
  };

  return {
    id: config.id,
    title: config.title,
    icon: config.icon,
    iconName: config.iconName,
    themeColor: config.themeColor,

    Component: () =>
      createElement(BaseModuleComponent, { config, queryKeys }),

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    createStore: () => {
      return createStore<BaseModuleStoreState>((set, get) => ({
        selectedId: undefined,
        searchQuery: "",
        ...(config.extendStore ? config.extendStore(
          (partial) => { set(partial as Partial<BaseModuleStoreState>); },
          () => get() as Record<string, unknown>,
        ) : {}),
      }));
    },

    setup: (runtime) => {
      // Prefetch first page
      const listParams = { limit: 50, offset: 0, ...config.rpcListParams };
      void runtime.queryClient.prefetchQuery({
        queryKey: queryKeys.list(listParams),
        queryFn: () =>
          runtime.transport.rpc(
            config.rpc?.list ?? `${config.id}.list`,
            listParams,
          ),
        staleTime: 30_000,
      });

      // Query invalidation on schema events
      const unsub = setupQueryInvalidation(
        runtime.transport,
        runtime.queryClient,
        [{ schemaIds: schemas, queryKeys: [queryKeys.all] }],
      );

      // Extra setup
      const extraCleanup = config.extraSetup?.(runtime);

      return () => {
        unsub();
        if (typeof extraCleanup === "function") extraCleanup();
      };
    },

    agent,

    // Declarative "Link to X" context-menu contribution (e.g. groups,
    // projects). Surfaced on the registry so `useEntityContextMenu` can
    // discover it across APP_MODULES without importing the module directly.
    entityLink: config.entityLink,
  };
}
